import { Num, Plural, T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import type { ValidationRunDTO } from '@erp/rpc';
import PaginatedGentlyTable, {
  type PaginationConfig,
  type TableColumn,
} from '@/components/common/PaginatedGentlyTable';
import {
  RunOutcomeBadge,
  formatValidationTimestamp,
  useTriggerLabels,
} from './validationRuleDisplay';

/** When a run started, and how long after that it stopped. */
function RunTimeCell({ run }: { run: ValidationRunDTO }) {
  return (
    <span className="text-sm whitespace-nowrap">
      {formatValidationTimestamp(run.startedAt)}
      {run.finishedAt === null && (
        <span className="ml-1.5 text-xs text-muted-foreground italic">
          <T context="a validation run that has started but not finished">
            still running
          </T>
        </span>
      )}
    </span>
  );
}

/** The outcome, with a failed run's own recorded message under it. */
function RunOutcomeCell({ run }: { run: ValidationRunDTO }) {
  return (
    <span className="flex flex-col items-start gap-1">
      <RunOutcomeBadge status={run.status} />
      {run.error !== null && (
        // The run's own recorded sentence, rendered verbatim.
        <span className="text-xs text-destructive">{run.error}</span>
      )}
    </span>
  );
}

/**
 * What the run measured — and, when it applies, how much of the schema it could
 * not reach. A run that covered twelve of fifteen divisions must not read here
 * as though it covered all of them.
 */
function RunStatsCell({ run }: { run: ValidationRunDTO }) {
  return (
    <span className="flex flex-col items-start gap-0.5 whitespace-nowrap">
      <span className="text-sm">
        <T context="one validation run's counts: how many groups disagreed out of how many, and how many of those got a proposed correction">
          <Num>{run.stats.violatingGroups}</Num> of{' '}
          <Num>{run.stats.groupsEvaluated}</Num>{' '}
          <Plural n={run.stats.groupsEvaluated} one="group" other="groups" /> ·{' '}
          <Num>{run.stats.correctionsProposed}</Num> proposed
        </T>
      </span>
      {run.skippedTables.length > 0 && (
        <span className="text-xs text-amber-600 dark:text-amber-500">
          <T context="note on a validation run that some division tables could not be evaluated">
            <Num>{run.skippedTables.length}</Num>{' '}
            <Plural
              n={run.skippedTables.length}
              one="division"
              other="divisions"
            />{' '}
            not evaluated
          </T>
        </span>
      )}
    </span>
  );
}

/** The review the run staged its corrections into, if it staged one. */
function RunReviewCell({ run }: { run: ValidationRunDTO }) {
  if (run.changeSetId === null) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  return (
    <Link
      to="/item-master/reviews/$changeSetId"
      params={{ changeSetId: run.changeSetId }}
      className="text-sm text-primary underline-offset-2 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      <T context="link from a validation run to the review its corrections were staged into">
        Open review
      </T>
    </Link>
  );
}

function useRunColumns(): TableColumn<ValidationRunDTO>[] {
  const gt = useGT();
  const triggerLabels = useTriggerLabels();

  return [
    {
      id: 'startedAt',
      label: gt('Started', {
        context: 'run history column, when the run started',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => <RunTimeCell run={row} />,
    },
    {
      id: 'finishedAt',
      label: gt('Finished', {
        context: 'run history column, when the run stopped',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => (
        <span className="text-sm whitespace-nowrap">
          {row.finishedAt === null
            ? '—'
            : formatValidationTimestamp(row.finishedAt)}
        </span>
      ),
    },
    {
      id: 'triggeredBy',
      label: gt('Trigger', {
        context: 'run history column, what started the run',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => (
        <span className="text-sm">{triggerLabels[row.triggeredBy]}</span>
      ),
    },
    {
      id: 'status',
      label: gt('Outcome', {
        context: 'run history column, how the run ended',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => <RunOutcomeCell run={row} />,
    },
    {
      id: 'stats',
      label: gt('Found', {
        context: 'run history column, what the run measured',
      }),
      className: 'py-3',
      render: (_value: unknown, row) => <RunStatsCell run={row} />,
    },
    {
      id: 'changeSetId',
      label: gt('Review', {
        context: 'run history column, the review the run staged',
      }),
      className: 'py-3',
      stopRowClick: true,
      render: (_value: unknown, row) => <RunReviewCell run={row} />,
    },
  ];
}

/**
 * Every time this rule has been evaluated for real, newest first — what each run
 * found, and the review it staged.
 */
export function ValidationRunHistoryTable({
  ruleId,
  runs,
  total,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  ruleId: string;
  runs: ValidationRunDTO[];
  total: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  const gt = useGT();
  const columns = useRunColumns();

  const paginationConfig: PaginationConfig = {
    enabled: total > pageSize,
    pagination: {
      pageSize,
      currentPage: page,
      hasNextPage: page * pageSize < total,
      hasPreviousPage: page > 1,
      totalItems: total,
      startItem: total === 0 ? 0 : (page - 1) * pageSize + 1,
      endItem: Math.min(page * pageSize, total),
    },
    onPageSizeChange,
    onPreviousPage: () => onPageChange(Math.max(1, page - 1)),
    onNextPage: () => onPageChange(page + 1),
    onFirstPage: () => onPageChange(1),
    onLastPage: () => onPageChange(Math.ceil(total / pageSize) || 1),
    onGoToPage: onPageChange,
    showRowsPerPage: true,
    showPageInfo: true,
    showPageNumbers: true,
  };

  return (
    <section className="space-y-2" aria-labelledby={`runs-${ruleId}`}>
      <h2 id={`runs-${ruleId}`} className="text-sm font-medium">
        <T context="heading of the run history section on a validation rule's page">
          Evaluation history
        </T>
      </h2>
      <PaginatedGentlyTable<ValidationRunDTO>
        data={runs}
        columns={columns}
        emptyMessage={gt('This rule has never been run.', {
          context: 'empty state of a validation rule’s run history',
        })}
        showSearch={false}
        showDownload={false}
        isLoading={isLoading}
        paginationConfig={paginationConfig}
        getRowKey={(row) => row.id}
      />
    </section>
  );
}
